import type { ComponentProps } from 'react';
import { Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCalendarDate } from '@/lib/dates';
import { PublicEventLogo } from './PublicEventLogo';
import { PublicEventNav } from './PublicEventNav';
import { GetNotifiedButton } from './GetNotifiedButton';
import { ScheduleContentTabs, type ScheduleContentTab } from './EventScheduleTabs';

interface PublicEventHeaderProps {
  slug: string;
  current: ComponentProps<typeof PublicEventNav>['current'];
  division?: string;
  event: {
    name?: string;
    start_date?: string | null;
    end_date?: string | null;
    venue_name?: string | null;
  };
  branding?: ComponentProps<typeof PublicEventLogo>['branding'];
  tabs: ScheduleContentTab[];
  teams: { id: string; name: string }[];
}

export function PublicEventHeader({
  slug, current, division, event, branding, tabs, teams,
}: PublicEventHeaderProps) {
  const start = formatCalendarDate(event.start_date, 'MMMM d, yyyy');
  const end = formatCalendarDate(event.end_date, 'MMMM d, yyyy');
  const dateRange = start && end && event.end_date !== event.start_date
    ? `${start} – ${end}`
    : start || end;

  return (
    <header className="mb-6 min-w-0 border-b pb-6">
      <div className="mb-4">
        <PublicEventLogo branding={branding} />
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-3xl font-bold text-brand">{event.name}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {teams.length > 0 && <GetNotifiedButton slug={slug} teams={teams} />}
          <PublicEventNav slug={slug} current={current} division={division} />
          <Button asChild size="sm" className="h-9 w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 hover:brightness-90">
            <a href="https://apps.apple.com/us/app/recruitlook-events/id6799738826" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 shrink-0" />
              <span className="flex flex-col items-start leading-none">
                <span className="text-xs font-normal">Get the app</span>
                <span className="mt-0.5 text-[10px] font-normal text-primary-foreground/80">Alerts for your teams</span>
              </span>
            </a>
          </Button>
        </div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {[dateRange, event.venue_name].filter(Boolean).join(' · ')}
      </p>
      <ScheduleContentTabs tabs={tabs} />
    </header>
  );
}